// Shared geometry for paint-area shapes.
//
// Every shape is expressed as a single SVG path `d` string in a 0–100 viewBox,
// so the SAME string drives both the visible SVG (`<path d>` / clip / mask) and
// the click hit-test (`new Path2D(d)`). This keeps the paint area's shape
// library in sync with the geometric-shape element instead of maintaining a
// separate hardcoded set.
//
// Faz 1 scope: the fillable subset of the shape library (polygon-based kinds +
// circle/ellipse) plus a free "polygon" with user-editable vertices. Curved
// decorative shapes (heart, crescent), lines and 3D solids are intentionally
// excluded — see the picker list below. Legacy paint-only values (cloud,
// flower, sun) and superseded names (diamond→rhombus, oval→ellipse) are still
// rendered for backward compatibility.
//
// NOTE: keep this file in sync with render-app/src/paintShapes.ts (the render
// app cannot import from the editor's src/).

export type PaintPoint = { x: number; y: number };

// Polygon vertex sets in the 0–100 viewBox. Mirrors POLYGON_POINTS_FOR_SHAPE in
// shapes.tsx (the geometric-shape element) so both elements draw the same forms.
const POLYGON_POINTS: Record<string, Array<[number, number]>> = {
  rectangle: [
    [5, 5],
    [95, 5],
    [95, 95],
    [5, 95],
  ],
  square: [
    [5, 5],
    [95, 5],
    [95, 95],
    [5, 95],
  ],
  triangle: [
    [50, 5],
    [95, 95],
    [5, 95],
  ],
  "right-triangle": [
    [5, 5],
    [5, 95],
    [95, 95],
  ],
  pentagon: [
    [50, 5],
    [95, 38],
    [78, 95],
    [22, 95],
    [5, 38],
  ],
  hexagon: [
    [25, 5],
    [75, 5],
    [95, 50],
    [75, 95],
    [25, 95],
    [5, 50],
  ],
  octagon: [
    [30, 5],
    [70, 5],
    [95, 30],
    [95, 70],
    [70, 95],
    [30, 95],
    [5, 70],
    [5, 30],
  ],
  star: [
    [50, 5],
    [61, 38],
    [95, 38],
    [68, 58],
    [78, 92],
    [50, 72],
    [22, 92],
    [32, 58],
    [5, 38],
    [39, 38],
  ],
  rhombus: [
    [50, 5],
    [95, 50],
    [50, 95],
    [5, 50],
  ],
  trapezoid: [
    [25, 15],
    [75, 15],
    [95, 85],
    [5, 85],
  ],
  parallelogram: [
    [25, 15],
    [95, 15],
    [75, 85],
    [5, 85],
  ],
  arrow: [
    [5, 40],
    [60, 40],
    [60, 20],
    [95, 50],
    [60, 80],
    [60, 60],
    [5, 60],
  ],
};

// Default vertices for a fresh free-polygon paint area (a pentagon the user can
// then drag into any form).
export const DEFAULT_FREE_POLYGON_POINTS: PaintPoint[] = [
  { x: 50, y: 8 },
  { x: 90, y: 38 },
  { x: 74, y: 90 },
  { x: 26, y: 90 },
  { x: 10, y: 38 },
];

function polyD(points: Array<[number, number]>): string {
  if (points.length < 2) return "";
  return "M " + points.map(([x, y]) => `${x},${y}`).join(" L ") + " Z";
}

// Ellipse as a closed path (two arcs) so circles/ellipses share the `d`-string
// pipeline with polygons.
function ellipseD(cx: number, cy: number, rx: number, ry: number): string {
  return (
    `M ${cx - rx},${cy} ` +
    `a ${rx},${ry} 0 1,0 ${rx * 2},0 ` +
    `a ${rx},${ry} 0 1,0 ${-rx * 2},0 Z`
  );
}

/**
 * SVG path `d` string (0–100 viewBox) for a paint-area shape.
 * @param shapeType canonical or legacy shape key.
 * @param points   only used for `polygon` (free vertices); falls back to a
 *                 default pentagon when absent/degenerate.
 */
export function getPaintShapePathD(
  shapeType: string,
  points?: PaintPoint[] | null
): string {
  switch (shapeType) {
    case "polygon": {
      const pts =
        points && points.length >= 3 ? points : DEFAULT_FREE_POLYGON_POINTS;
      return polyD(pts.map((p) => [p.x, p.y]));
    }
    case "circle":
      return ellipseD(50, 50, 48, 48);
    case "ellipse":
    case "oval": // legacy alias
      return ellipseD(50, 50, 48, 34);
    case "diamond": // legacy alias for rhombus
      return polyD(POLYGON_POINTS.rhombus);
    // ── Legacy paint-only decorative shapes (kept, not offered in picker) ──
    case "cloud":
      return "M30 50 Q20 50 20 60 Q20 70 30 70 L70 70 Q80 70 80 60 Q80 50 70 50 Q70 40 60 40 Q50 40 50 50 Q40 50 30 50 Z";
    case "flower":
      return [
        ellipseD(50, 50, 8, 8),
        ellipseD(50, 30, 6, 12),
        ellipseD(50, 70, 6, 12),
        ellipseD(30, 50, 12, 6),
        ellipseD(70, 50, 12, 6),
      ].join(" ");
    case "sun":
      return ellipseD(50, 50, 20, 20);
    default: {
      const pts = POLYGON_POINTS[shapeType];
      // Unknown/unsupported (e.g. excluded line/heart/3D) → safe rectangle.
      return polyD(pts ?? POLYGON_POINTS.rectangle);
    }
  }
}

/** True when the shape's fill/hit-test is driven by free vertices. */
export function isFreePolygonShape(shapeType: string): boolean {
  return shapeType === "polygon";
}

export interface PaintShapeOption {
  value: string;
  labelTr: string;
  labelEn: string;
}

// Shapes offered in the paint-area inspector picker (Faz 1). Order groups the
// free polygon first (the flexible "any shape" option), then presets.
export const PAINT_SHAPE_PICKER_OPTIONS: PaintShapeOption[] = [
  { value: "polygon", labelTr: "Serbest Çokgen (düzenlenebilir)", labelEn: "Free Polygon (editable)" },
  { value: "rectangle", labelTr: "Dikdörtgen", labelEn: "Rectangle" },
  { value: "square", labelTr: "Kare", labelEn: "Square" },
  { value: "circle", labelTr: "Daire", labelEn: "Circle" },
  { value: "ellipse", labelTr: "Elips", labelEn: "Ellipse" },
  { value: "triangle", labelTr: "Üçgen", labelEn: "Triangle" },
  { value: "right-triangle", labelTr: "Dik Üçgen", labelEn: "Right Triangle" },
  { value: "pentagon", labelTr: "Beşgen", labelEn: "Pentagon" },
  { value: "hexagon", labelTr: "Altıgen", labelEn: "Hexagon" },
  { value: "octagon", labelTr: "Sekizgen", labelEn: "Octagon" },
  { value: "star", labelTr: "Yıldız", labelEn: "Star" },
  { value: "rhombus", labelTr: "Eşkenar Dörtgen", labelEn: "Rhombus" },
  { value: "trapezoid", labelTr: "Yamuk", labelEn: "Trapezoid" },
  { value: "parallelogram", labelTr: "Paralelkenar", labelEn: "Parallelogram" },
  { value: "arrow", labelTr: "Ok", labelEn: "Arrow" },
  // Decorative paint-only shapes carried over from the original paint library.
  { value: "cloud", labelTr: "Bulut", labelEn: "Cloud" },
  { value: "flower", labelTr: "Çiçek", labelEn: "Flower" },
  { value: "sun", labelTr: "Güneş", labelEn: "Sun" },
];
